import {
  FOLLTROPIN_2X_DRUG_NAME,
  FOLLTROPIN_DRUG_NAME,
  NO_STIM_SCHEDULE_NAME,
} from '../constants';

/**
 * Minimal shape of a drug needed to default the FSH drug fields. The donor
 * schedule, SO schedule and drug search all return at least `id` and `name`.
 */
export interface DrugLike {
  id: string;
  name?: string | null;
}

export interface FshDrug1Payload {
  fshId: string | undefined;
  drug: DrugLike | null;
  /** How Drug 1 was resolved. Used by callers to select the appropriate toast. */
  source: 'saved' | 'default' | 'none';
}

export interface FshDrug2Payload {
  fsh2Id: string | undefined;
  drug2: DrugLike | null;
  /** How Drug 2 was resolved. Used by callers to select the appropriate toast. */
  source: 'saved' | 'so' | 'none';
}

/**
 * Returns the FSH Drug 1 default name driven by the Schedule Master's
 * `twiceConcentration` flag. Only `true` -> "Folltropin 2X"; everything else
 * (`false`, `null`, `undefined`) -> "Folltropin".
 */
export function getFolltropinDrugName(
  twiceConcentration: boolean | null | undefined,
): string {
  return twiceConcentration ? FOLLTROPIN_2X_DRUG_NAME : FOLLTROPIN_DRUG_NAME;
}

/**
 * Returns `true` when a schedule master name represents a "NO STIM" protocol.
 * Matches any name that starts with "NO STIM" (case-insensitive), covering
 * variants like "NO STIM", "NO STIM xxx", "NO STIM ETH", etc.
 */
export function isNoStimSchedule(name: string | null | undefined): boolean {
  return name?.toUpperCase().startsWith(NO_STIM_SCHEDULE_NAME) ?? false;
}

/** Finds an exact (case-sensitive) name match within a list of drugs. */
export function findExactDrugByName<T extends DrugLike>(
  drugs: readonly T[] | null | undefined,
  name: string,
): T | undefined {
  return drugs?.find((drug) => drug.name === name);
}

/**
 * Returns `true` when a drug name represents a Folltropin auto-default value
 * (including the absent / not-yet-set case). This is used to decide whether a
 * Schedule Master change should overwrite the current FSH Drug 1 value:
 *   - no drug yet (null / undefined / empty) → safe to default
 *   - already "Folltropin" or "Folltropin 2X" (set by a prior auto-default) → safe to re-default
 *   - any other name (user-selected or DB-saved) → keep it, do not overwrite
 */
export function isFolltropinDefaultDrug(
  drugName: string | null | undefined,
): boolean {
  return (
    !drugName ||
    drugName === FOLLTROPIN_DRUG_NAME ||
    drugName === FOLLTROPIN_2X_DRUG_NAME
  );
}

/**
 * Resolves FSH Drug 1 when the `DS_Default_SO` flag is enabled.
 *
 * Precedence:
 * 1. Donor Schedule saved value (`source: 'saved'`)
 * 2. Folltropin / Folltropin 2X default based on `twiceConcentration` (`source: 'default'`)
 * 3. Nothing resolved (`source: 'none'`)
 *
 * There is no SO Schedule fallback for Drug 1 — callers must check for a valid
 * schedule master before calling this function.
 */
export async function resolveFshDrug1({
  savedFshId,
  savedDrug,
  twiceConcentration,
  resolveDrugByName,
}: {
  savedFshId: string | null | undefined;
  savedDrug: DrugLike | null | undefined;
  twiceConcentration: boolean | null | undefined;
  resolveDrugByName: (name: string) => Promise<DrugLike | undefined>;
}): Promise<FshDrug1Payload> {
  // 1. Donor Schedule saved value.
  if (savedFshId) {
    return { fshId: savedFshId, drug: savedDrug ?? null, source: 'saved' };
  }

  // 2. Twice-concentration default — null/undefined/false all mean "no 2X".
  {
    const name = getFolltropinDrugName(twiceConcentration);
    const resolved = await resolveDrugByName(name);
    if (resolved) {
      return { fshId: resolved.id, drug: resolved, source: 'default' };
    }
  }

  return { fshId: undefined, drug: null, source: 'none' };
}

/**
 * Resolves FSH Drug 2 when the `DS_Default_SO` flag is enabled.
 *
 * Precedence:
 * 1. Donor Schedule saved value (`source: 'saved'`)
 * 2. SO Schedule fallback (`source: 'so'`)
 * 3. Nothing resolved (`source: 'none'`)
 *
 * The twice-concentration default applies to FSH Drug 1 only.
 */
export function resolveFshDrug2({
  savedFsh2Id,
  savedDrug2,
  soDrug2,
}: {
  savedFsh2Id: string | null | undefined;
  savedDrug2: DrugLike | null | undefined;
  soDrug2: DrugLike | null | undefined;
}): FshDrug2Payload {
  // 1. Donor Schedule saved value.
  if (savedFsh2Id) {
    return { fsh2Id: savedFsh2Id, drug2: savedDrug2 ?? null, source: 'saved' };
  }

  // 2. SO Schedule fallback.
  if (soDrug2) {
    return { fsh2Id: soDrug2.id, drug2: soDrug2, source: 'so' };
  }

  return { fsh2Id: undefined, drug2: null, source: 'none' };
}
